/**
 * API base-url policy for the widget loader.
 *
 * The loader sends a partner's embed/session bearer to whatever origin
 * `apiBaseUrl` points at, so the rule for which origins are acceptable is a
 * security boundary, not a convenience. It lives in its own module so the
 * policy is unit-testable in isolation (the loader itself is an IIFE bundle
 * with no exported internals).
 */

// Origins we are willing to send a partner's embed token to. If a host page
// is XSS'd and an attacker swaps `data-api-base-url` to their own server,
// this list keeps us from leaking the bearer to that origin.
export const ALLOWED_API_ORIGINS = new Set([
  'https://api.hatched.live',
  'https://api.staging.hatched.live',
]);
export const PROD_API_BASE_URL = 'https://api.hatched.live/api/v1';
export const STAGING_API_BASE_URL = 'https://api.staging.hatched.live/api/v1';

export function isLoopbackHostname(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    hostname === '::1' ||
    hostname.endsWith('.localhost')
  );
}

export function isAllowedApiBaseUrl(value: string): boolean {
  try {
    const url = new URL(value);
    if (ALLOWED_API_ORIGINS.has(url.origin)) return true;
    // Local development / preview: integrators routinely point the loader at a
    // dev API (`http://localhost:3000/api/v1`) while wiring up the embed. An
    // attacker can't meaningfully redirect a victim's bearer to the victim's
    // own loopback, so loopback origins are safe to allow — and unblocking
    // them is what makes local previews work. Public origins stay locked to
    // the Hatched API above so a host-page XSS still can't siphon the token.
    if (
      (url.protocol === 'http:' || url.protocol === 'https:') &&
      isLoopbackHostname(url.hostname)
    ) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

// Pick the default API base from the loader's own URL so the bundle a partner
// installs is the same code path-routed to the right environment. Two layouts
// are recognised:
//
//   * `cdn.hatched.live/staging/widget.js` → prod-host CDN, staging path
//     prefix. Used while staging shares the prod R2 bucket.
//   * `cdn.staging.hatched.live/widget.js` → staging-host CDN, if that
//     DNS/custom domain is provisioned.
//
// Any other shape (including a missing/foreign script src) falls back to
// production. Partners can still override via `data-api-base-url`, but the
// default no longer leaks staging traffic into the prod API.
export function inferDefaultApiBaseUrl(
  scriptSrc: string | undefined | null,
): string {
  if (!scriptSrc) return PROD_API_BASE_URL;
  try {
    const url = new URL(scriptSrc);
    if (url.hostname.startsWith('cdn.staging.')) return STAGING_API_BASE_URL;
    if (url.pathname.startsWith('/staging/')) return STAGING_API_BASE_URL;
    return PROD_API_BASE_URL;
  } catch {
    return PROD_API_BASE_URL;
  }
}
